import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Search, Shield, ArrowRight } from 'lucide-react';
import ComingSoonButton from './ComingSoonButton';
import { useComingSoon } from './ComingSoonProvider';

const ServicesGrid: React.FC = () => {
  const { openModal } = useComingSoon();

  const services = [
    {
      icon: FileText,
      title: 'Document Automation',
      description: 'Streamline document capture, extraction, and processing with intelligent automation workflows.',
      features: ['Multi-format support', 'Batch processing', 'Real-time validation', 'Custom workflows'],
      color: 'from-primary-500 to-primary-600'
    },
    {
      icon: Search,
      title: 'Intelligent Review',
      description: 'AI-powered document analysis and review with advanced pattern recognition and compliance checking.',
      features: ['Content analysis', 'Compliance validation', 'Risk assessment', 'Automated approvals'],
      color: 'from-accent-500 to-accent-600'
    },
    {
      icon: Shield,
      title: 'Compliance Processing',
      description: 'Enterprise-grade security and regulatory compliance for document handling and workflow automation.',
      features: ['Data encryption', 'Audit trails', 'Regulatory reporting'],
      color: 'from-primary-500 to-accent-500'
    }
  ];

  return (
    <section className="py-20 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold font-display mb-6"
          >
            AI Agent Solutions
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-xl text-gray-300 max-w-3xl mx-auto"
          >
            Comprehensive AI agent solutions designed for businesses across all industries and use cases.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
              className="group relative"
            >
              <div className="glass-effect rounded-2xl p-8 h-full card-hover border border-dark-700 group-hover:border-primary-500/50 transition-all duration-300">
                {/* Icon */}
                <div className={`w-16 h-16 bg-gradient-to-r ${service.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <service.icon size={32} className="text-white" />
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold mb-4 text-white">{service.title}</h3>
                <p className="text-gray-400 mb-6 leading-relaxed">{service.description}</p>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center text-gray-300">
                      <div className="w-2 h-2 bg-primary-400 rounded-full mr-3"></div>
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                {/* <button className="inline-flex items-center text-primary-400 hover:text-primary-300 font-medium group-hover:translate-x-1 transition-all duration-200">
                  Learn More
                  <ArrowRight size={16} className="ml-2" />
                </button> */}
              </div>

              {/* Gradient overlay on hover */}
              <div className="absolute inset-0 bg-gradient-to-r from-primary-500/5 to-accent-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="glass-effect rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold mb-4 text-white">Ready to Transform Your Business Processes?</h3>
            <p className="text-gray-300 mb-6">
              Join leading enterprises that trust Keltnine for their AI agent automation needs.
            </p>
            <div className="flex justify-center">
              <ComingSoonButton onClick={openModal}>
                Schedule a Demo
              </ComingSoonButton>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesGrid; 